import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Modal,
  Select,
  SelectChangeEvent,
  Typography
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import { Form, Formik } from 'formik';
import CloseIcon from '@mui/icons-material/Close';
import { CREATE_PROJECT, GET_SEARCH_MEMBERS } from '../../gql';
import { validationSchema } from './validations/create-project-validation-schema';
import { errorToast, successToast } from '../../../common/utils/alert';
import { errorMessages } from '../../../common/utils/constants';
import NumberInput from '../../../common/widgets/number-input';
import TextFieldComponent from '../../../common/widgets/text-field';
import DatePickerComponent from '../../../common/widgets/date-picker';
import AutoCompleteSearchComponent from '../../../common/widgets/auto-complete-search';
import { format } from 'date-fns';
import { ICreateProjectValues } from '../../type';
import { TabInfo } from '../../../common/utils/interfaces';
import ProjectAllocation from '../project-allocation';
import { useAppSelector } from '../../../../redux/hooks';

export const projectTypes: TabInfo[] = [
  {
    label: 'Fixed-Time Fixed-Cost Project',
    value: 'FixedTimeFixedCostProject'
  },
  { label: 'Time & Material Project', value: 'TimeAndMaterialProject' },
  {
    label: 'Time & Material with Budget Cap',
    value: 'TimeAndMaterialWithBudgetCap'
  }
];

interface ICreateProject {
  createProjectCallback: (id: string) => void;
  closeRequestForm: () => void;
  isCreateModalVisible: boolean;
}
const CreateProjectModal = ({
  isCreateModalVisible,
  closeRequestForm,
  createProjectCallback
}: ICreateProject): JSX.Element => {
  const { profileData } = useAppSelector((state) => state.profileData);
  const [initialFormState] = useState<ICreateProjectValues>({
    projectName: '',
    clientName: '',
    tentativeStartDate: '',
    tentativeEndDate: '',
    projectType: projectTypes[0].label,
    hypercarePeriod: 0,
    projectMember: '',
    hoursPerWeekForPMOrDR: 0,
    isMaximumAllocation: false
  });
  const [searchingProjectManagerId, setSearchingProjectManagerId] =
    useState<string>();
  const [
    hoursPerWeekForPMOrDRAllocationValue,
    sethoursPerWeekForPMOrDRAllocationValue
  ] = useState<string>();
  const [createProjectMutation] = useMutation(CREATE_PROJECT);
  const handleCancel = (): void => {
    closeRequestForm();
    setSearchingProjectManagerId('');
    sethoursPerWeekForPMOrDRAllocationValue('');
  };

  const handleSubmit = (values: ICreateProjectValues): void => {
    closeRequestForm();

    createProjectMutation({
      variables: {
        projectName: values.projectName,
        clientName: values.clientName,
        tentativeStartDate: format(
          new Date(values.tentativeStartDate),
          "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
        ),
        tentativeEndDate: format(
          new Date(values.tentativeEndDate),
          "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'"
        ),
        projectType: values.projectType,
        hypercarePeriod: values.hypercarePeriod,
        projectManagerId: values.projectMember,
        hoursPerWeekForPMOrDR: values.hoursPerWeekForPMOrDR,
        createdById: profileData?.memberId
      }
    })
      .then((response) => {
        successToast('Project Created Successfully');
        const createdProject = JSON.parse(response.data.createProject);
        createProjectCallback(createdProject.project.id);
        setSearchingProjectManagerId('');
        sethoursPerWeekForPMOrDRAllocationValue('');
      })
      .catch(() => {
        errorToast(errorMessages.ErrormessageCreatingProject);
      });
  };

  return (
    <Modal
      open={isCreateModalVisible}
      onClose={closeRequestForm}
      sx={{
        marginTop: '25px',
        overflow: 'scroll'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '24px',
          p: '35px'
        }}
      >
        <Formik
          onSubmit={handleSubmit}
          initialValues={initialFormState}
          validationSchema={validationSchema}
        >
          {({ setFieldValue, errors, touched, values }) => (
            <Form className={'form'} data-testid="form">
              <Box
                sx={{
                  maxWidth: '678px',
                  padding: '5px 5px 5px 5px',
                  borderRadius: '28px',
                  boxShadow: 'none',
                  background:
                    'linear-gradient(0deg, rgba(41, 93, 171, 0.11), rgba(41, 93, 171, 0.11)), #FDFBFF'
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton onClick={handleCancel}>
                    <CloseIcon />
                  </IconButton>
                </Box>

                <Box
                  sx={{
                    padding: '0px 20px'
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '24px',
                      fontWeight: 400,
                      color: '#1A1B1F'
                    }}
                  >
                    Create Project
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <TextFieldComponent
                      label={'Project Name'}
                      value={values.projectName}
                      onChange={(value) => {
                        void setFieldValue('projectName', value);
                      }}
                      touched={touched.projectName}
                      errors={errors.projectName}
                    />
                    <TextFieldComponent
                      label={'ClientName(Optional)'}
                      value={values.clientName}
                      onChange={(value) => {
                        void setFieldValue('clientName', value);
                      }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <Typography sx={{ marginTop: '15px' }}>
                        Project Type
                      </Typography>
                      <Select
                        labelId="project-type-select-label"
                        value={values.projectType}
                        onChange={(event: SelectChangeEvent) => {
                          void setFieldValue('projectType', event.target.value);
                        }}
                        sx={{ marginTop: '10px' }}
                      >
                        {projectTypes.map((tab) => (
                          <MenuItem key={tab.label} value={tab.label}>
                            {tab.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.projectType && errors.projectType && (
                        <Typography
                          sx={{
                            color: 'red',
                            fontSize: '10px'
                          }}
                        >
                          Project type is mandatory.
                        </Typography>
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        flexWrap: 'wrap',
                        gap: '15px'
                      }}
                    >
                      <Box sx={{ flexGrow: 1 }}>
                        <DatePickerComponent
                          label={'Tentative Start Date'}
                          format={'MM/dd/yyyy'}
                          onChange={function (event: Date | null): void {
                            if (event) {
                              void setFieldValue(
                                'tentativeStartDate',
                                format(event, 'MM/dd/yyyy')
                              );
                            }
                          }}
                          touched={touched.tentativeStartDate}
                          errors={errors.tentativeStartDate}
                        />
                      </Box>

                      <Box sx={{ flexGrow: 1 }}>
                        <DatePickerComponent
                          label={'Tentative End Date'}
                          format={'MM/dd/yyyy'}
                          onChange={function (event: Date | null): void {
                            if (event) {
                              void setFieldValue(
                                'tentativeEndDate',
                                format(event, 'MM/dd/yyyy')
                              );
                            }
                          }}
                          touched={touched.tentativeEndDate}
                          errors={errors.tentativeEndDate}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <NumberInput
                        label={'Hypercare Period'}
                        defaultValue={initialFormState.hypercarePeriod}
                        onChange={(value) => {
                          void setFieldValue('hypercarePeriod', value);
                        }}
                        min={0}
                        max={999}
                        text={'Weeks'}
                      />
                      <Typography sx={{ color: 'red', fontSize: '0.7rem' }}>
                        {touched.hypercarePeriod && errors.hypercarePeriod}
                      </Typography>
                    </Box>
                    <AutoCompleteSearchComponent
                      label={'Project Manager/Delivery Responsible'}
                      query={{
                        query: GET_SEARCH_MEMBERS,
                        getter: 'getMembers'
                      }}
                      optionLabelKey={'displayName'}
                      onChange={(newValue) => {
                        void setFieldValue('projectMember', newValue?.id);
                        if (newValue) {
                          setSearchingProjectManagerId(newValue.id.toString());
                          void setFieldValue('projectMember', newValue.id);
                        } else {
                          setSearchingProjectManagerId('');
                        }
                      }}
                      touched={touched.projectMember}
                      errors={errors.projectMember}
                    />

                    <Box
                      sx={{
                        mt: '20px'
                      }}
                    >
                      {searchingProjectManagerId && (
                        <ProjectAllocation
                          memberId={searchingProjectManagerId}
                          isAdd={true}
                          value={hoursPerWeekForPMOrDRAllocationValue}
                          onChange={(value) => {
                            const newValue = parseInt(value, 10);
                            sethoursPerWeekForPMOrDRAllocationValue(value);
                            void setFieldValue(
                              'hoursPerWeekForPMOrDR',
                              newValue
                            );
                          }}
                          touched={touched.hoursPerWeekForPMOrDR}
                          errors={errors.hoursPerWeekForPMOrDR}
                          setIsMaximumAllocation={(value) => {
                            void setFieldValue('isMaximumAllocation', value);
                          }}
                        />
                      )}
                    </Box>

                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row-reverse',
                        gap: '8px',
                        marginTop: '30px',
                        marginBottom: '10px'
                      }}
                    >
                      <Button
                        type="submit"
                        sx={{
                          fontWeight: 500,
                          letterSpacing: '0.1px',
                          fontSize: '18px',
                          textTransform: 'capitalize'
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        onClick={handleCancel}
                        sx={{
                          fontWeight: 500,
                          letterSpacing: '0.1px',
                          fontSize: '18px',
                          textTransform: 'capitalize'
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};
export default CreateProjectModal;
